/**
 * Anniversaires du jour — vue sociale style mur Facebook.
 * - Liste des étudiants qui fêtent aujourd'hui (sauf ceux ayant désactivé la visibilité)
 * - Section « Prochains anniversaires (5 jours à venir) » sous la liste du jour
 * - Détail : tous les messages reçus + commentaires (1 niveau de réponse) + likes
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Cake, Heart, Send, Loader2, Trash2,
  MessageCircle, CornerDownRight, Calendar, Smile, X,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

// ─── Composants partagés (lightbox photo + sélecteur emoji) ────────

const EMOJIS_SOUHAITS = [
  '🎂', '🎉', '🎊', '🥳', '🎁', '🎈',
  '❤️', '💖', '💕', '🌹', '🌟', '✨',
  '🙌', '👏', '🤗', '😊', '😄', '🥰',
  '🍰', '🧁', '🍾', '🥂', '🎵', '☀️',
];

function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      >
        <X size={20} />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
      />
    </div>
  );
}

function EmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  return (
    <div className="absolute bottom-full mb-2 left-0 z-30 bg-gray-800 border border-gray-700 rounded-xl shadow-xl p-2 w-64">
      <div className="grid grid-cols-6 gap-1">
        {EMOJIS_SOUHAITS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => { onPick(e); onClose(); }}
            className="text-xl p-1.5 rounded hover:bg-gray-700 active:bg-gray-600"
          >
            <span className="emoji-hover-bounce">{e}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Liste du jour + prochains ──────────────────────────────────

export default function AnniversairesIndex() {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prochains, setProchains] = useState<Record<string, any[]>>({});

  useEffect(() => {
    (async () => {
      try {
        const r = await portailApi.anniversairesDuJour();
        setItems(r.results);
      } catch (e: any) {
        setError(e?.message || 'Erreur');
      } finally {
        setLoading(false);
      }
      try {
        const p = await portailApi.anniversairesProchains(5);
        setProchains(p.par_date);
      } catch { /* silencieux */ }
    })();
  }, []);

  const prochainsEntries = Object.entries(prochains).filter(([, arr]) => arr.length > 0);

  return (
    <PortailLayout showHeader title="Anniversaires du jour" fullWidth>
      <div className="space-y-3">
        <button
          onClick={() => navigate('/portail/accueil')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        {loading && (
          <div className="text-center py-12">
            <Loader2 className="inline-block animate-spin text-pink-400" size={28} />
          </div>
        )}
        {error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-300">{error}</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-10 text-gray-500 text-sm">
            <Cake size={40} className="inline-block text-gray-700 mb-3" />
            <div>Aucun anniversaire aujourd'hui parmi les étudiants visibles.</div>
            <div className="text-[11px] mt-2">Les étudiants qui ont désactivé la visibilité ne sont pas listés.</div>
          </div>
        )}

        {items.length > 0 && (
          <div className="text-xs text-gray-400 mb-2">
            🎂 <strong>{items.length}</strong> étudiant(s) fête{items.length > 1 ? 'nt' : ''} aujourd'hui
          </div>
        )}

        {items.map((etu) => (
          <button
            key={etu.matricule}
            onClick={() => navigate(`/portail/anniversaires/${etu.matricule}`)}
            className="w-full text-left bg-gradient-to-br from-pink-900/30 to-purple-900/20 border border-pink-700/40 hover:border-pink-500 rounded-xl p-3 transition-colors flex items-center gap-3"
          >
            {etu.photo_url ? (
              <img src={etu.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-pink-500/50" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-pink-600 text-white flex items-center justify-center text-lg font-bold">
                {(etu.prenoms?.[0] || '') + (etu.nom?.[0] || '')}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-gray-100 truncate">
                <span className="emoji-bounce mr-1">🎂</span>{etu.prenoms} {etu.nom} {etu.est_moi && <span className="text-pink-400 text-xs">(c'est vous !)</span>}
              </div>
              <div className="text-[11px] text-gray-400">
                {etu.classe} {etu.age && <>· {etu.age} ans aujourd'hui</>}
              </div>
              <div className="text-[10px] text-pink-300 mt-1">
                {etu.nb_messages > 0
                  ? `${etu.nb_messages} souhait${etu.nb_messages > 1 ? 's' : ''} reçu${etu.nb_messages > 1 ? 's' : ''}`
                  : 'Soyez le premier à souhaiter'}
              </div>
            </div>
          </button>
        ))}

        {/* Prochains anniversaires (5 jours) */}
        {prochainsEntries.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide text-gray-400">
              <Calendar size={14} /> À venir cette semaine
            </div>
            <div className="space-y-3">
              {prochainsEntries.map(([dateIso, arr]) => {
                const d = new Date(dateIso + 'T00:00:00');
                const jr = arr[0]?.jours_restants ?? 0;
                let libelleQuand: string;
                if (jr === 1) libelleQuand = 'Demain';
                else if (jr === 2) libelleQuand = 'Après-demain';
                else libelleQuand = `Dans ${jr} jours`;
                const dateCourte = d.toLocaleDateString('fr-FR', {
                  weekday: 'long', day: 'numeric', month: 'long',
                });
                return (
                  <div key={dateIso} className="bg-gray-900 border border-gray-800 rounded-xl p-3">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-xs font-semibold text-pink-300">{libelleQuand}</span>
                      <span className="text-[10px] text-gray-500 capitalize">· {dateCourte}</span>
                    </div>
                    <div className="space-y-1.5">
                      {arr.map((e: any) => (
                        <button
                          key={e.matricule}
                          type="button"
                          onClick={() => navigate(`/portail/anniversaires/${e.matricule}`)}
                          className="w-full text-left flex items-center gap-2.5 hover:bg-gray-800/60 rounded-lg px-1.5 py-1 transition-colors"
                        >
                          {e.photo_url ? (
                            <img src={e.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-700 text-gray-200 flex items-center justify-center text-[10px] font-bold">
                              {(e.prenoms?.[0] || '') + (e.nom?.[0] || '')}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-100 truncate">{e.prenoms} {e.nom}</div>
                            <div className="text-[10px] text-gray-500">{e.classe}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PortailLayout>
  );
}

// ─── Bloc commentaires d'un message ─────────────────────────────

interface CommentaireProps {
  c: any;
  onReload: () => void;
  isReply?: boolean;
}

function Commentaire({ c, onReload, isReply = false }: CommentaireProps) {
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const toggleLike = async () => {
    try {
      await portailApi.anniversaireCommentaireLike(c.id, !c.a_like);
      onReload();
    } catch { /* ignore */ }
  };

  const supprimer = async () => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await portailApi.anniversaireCommentaireSupprimer(c.id);
      onReload();
    } catch { /* ignore */ }
  };

  const envoyerReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await portailApi.anniversaireCommentaireCreer(c.message_id, {
        contenu: replyText.trim(),
        parent_id: c.id,
      });
      setReplyText('');
      setReplying(false);
      onReload();
    } catch { /* ignore */ } finally {
      setSending(false);
    }
  };

  return (
    <div className={`flex items-start gap-2 ${isReply ? 'pl-6' : ''}`}>
      {isReply && <CornerDownRight size={14} className="text-gray-600 flex-shrink-0 mt-2" />}
      {c.auteur.est_admin ? (
        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">2i</div>
      ) : c.auteur.photo_url ? (
        <button type="button" onClick={() => setZoom(true)} className="flex-shrink-0">
          <img src={c.auteur.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
        </button>
      ) : (
        <div className="w-7 h-7 rounded-full bg-purple-700 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
          {(c.auteur.prenoms?.[0] || '') + (c.auteur.nom?.[0] || '')}
        </div>
      )}
      {zoom && c.auteur.photo_url && (
        <ImageLightbox src={c.auteur.photo_url} alt={`${c.auteur.prenoms} ${c.auteur.nom}`} onClose={() => setZoom(false)} />
      )}
      <div className="flex-1 min-w-0">
        <div className="bg-gray-800 rounded-2xl px-3 py-2">
          <div className="text-[11px] font-semibold text-gray-200">
            {c.auteur.est_admin ? (
              <span className="inline-flex items-center gap-1">
                {c.auteur.nom}
                <span className="text-[8px] px-1 py-0.5 bg-blue-700 text-blue-100 rounded">SCOLARITÉ</span>
              </span>
            ) : (
              <>{c.auteur.prenoms} {c.auteur.nom}</>
            )}
          </div>
          <div className="text-[13px] text-gray-100 whitespace-pre-wrap break-words">{c.contenu}</div>
        </div>
        <div className="flex items-center gap-3 mt-1 ml-2 text-[10px] text-gray-500">
          <span>{new Date(c.date_creation).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          <button
            onClick={toggleLike}
            className={`inline-flex items-center gap-1 ${c.a_like ? 'text-red-400' : 'hover:text-red-400'}`}
          >
            <Heart size={10} className={c.a_like ? 'fill-current' : ''} />
            {c.nb_likes > 0 ? c.nb_likes : 'J\'aime'}
          </button>
          {!isReply && (
            <button onClick={() => setReplying((v) => !v)} className="hover:text-blue-400">
              Répondre
            </button>
          )}
          {c.est_de_moi && (
            <button onClick={supprimer} className="hover:text-red-400 inline-flex items-center gap-1">
              <Trash2 size={10} /> Suppr.
            </button>
          )}
        </div>
        {/* Réponses (1 niveau) */}
        {!isReply && c.replies && c.replies.length > 0 && (
          <div className="mt-2 space-y-2">
            {c.replies.map((r: any) => (
              <Commentaire key={r.id} c={r} onReload={onReload} isReply />
            ))}
          </div>
        )}
        {/* Formulaire de réponse */}
        {replying && !isReply && (
          <div className="mt-2 pl-2 flex items-center gap-1.5 relative">
            <button
              type="button"
              onClick={() => setShowEmoji((v) => !v)}
              className="p-1.5 text-gray-400 hover:text-amber-400"
              title="Insérer un émoji"
            >
              <Smile size={14} />
            </button>
            {showEmoji && (
              <EmojiPicker
                onPick={(emo) => setReplyText((v) => v + emo)}
                onClose={() => setShowEmoji(false)}
              />
            )}
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Répondre…"
              maxLength={500}
              className="flex-1 px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-full text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
              onKeyDown={(e) => { if (e.key === 'Enter') envoyerReply(); }}
            />
            <button
              onClick={envoyerReply}
              disabled={sending || !replyText.trim()}
              className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-full"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Bloc message (souhait) + ses commentaires ─────────────────

interface MessageBlocProps {
  m: any;
  onReload: () => void;
}

function MessageBloc({ m, onReload }: MessageBlocProps) {
  const [comms, setComms] = useState<any[] | null>(null);
  const [chargement, setChargement] = useState(false);
  const [show, setShow] = useState(false);
  const [comNew, setComNew] = useState('');
  const [sending, setSending] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const chargerComms = useCallback(async () => {
    setChargement(true);
    try {
      const r = await portailApi.anniversaireCommentaires(m.id);
      setComms(r.results);
    } catch { setComms([]); } finally {
      setChargement(false);
    }
  }, [m.id]);

  const toggleShow = async () => {
    const nv = !show;
    setShow(nv);
    if (nv && comms === null) await chargerComms();
  };

  const toggleLike = async () => {
    try {
      await portailApi.anniversaireMessageLike(m.id, !m.a_like);
      onReload();
    } catch { /* ignore */ }
  };

  const supprimer = async () => {
    if (!window.confirm('Supprimer mon message ?')) return;
    try {
      await portailApi.anniversaireMessageSupprimer(m.id);
      onReload();
    } catch { /* ignore */ }
  };

  const envoyerComm = async () => {
    if (!comNew.trim()) return;
    setSending(true);
    try {
      await portailApi.anniversaireCommentaireCreer(m.id, { contenu: comNew.trim() });
      setComNew('');
      await chargerComms();
    } catch { /* ignore */ } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-3">
      <div className="flex items-start gap-2.5">
        {m.auteur.est_admin ? (
          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
            2i
          </div>
        ) : m.auteur.photo_url ? (
          <button type="button" onClick={() => setZoom(true)} className="flex-shrink-0">
            <img src={m.auteur.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          </button>
        ) : (
          <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
            {(m.auteur.prenoms?.[0] || '') + (m.auteur.nom?.[0] || '')}
          </div>
        )}
        {zoom && m.auteur.photo_url && (
          <ImageLightbox src={m.auteur.photo_url} alt={`${m.auteur.prenoms} ${m.auteur.nom}`} onClose={() => setZoom(false)} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-100">
              {m.auteur.est_admin ? (
                <span className="inline-flex items-center gap-1">
                  {m.auteur.nom}
                  <span className="text-[9px] px-1 py-0.5 bg-blue-700 text-blue-100 rounded">SCOLARITÉ</span>
                </span>
              ) : (
                <>{m.auteur.prenoms} {m.auteur.nom}</>
              )}
            </span>
            {!m.auteur.est_admin && m.auteur.classe && (
              <span className="text-[10px] text-gray-500">· {m.auteur.classe}</span>
            )}
            <span className="text-[10px] text-gray-500">
              · {new Date(m.date_creation).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="text-sm text-gray-200 mt-1 whitespace-pre-wrap break-words">
            {m.type_souhait === 'like'
              ? <span className="emoji-heart text-2xl">❤️</span>
              : m.contenu}
          </div>
          <div className="flex items-center gap-2 mt-2 text-[11px]">
            <button
              onClick={toggleLike}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${
                m.a_like
                  ? 'bg-red-900/30 text-red-300 border border-red-700/50'
                  : 'bg-gray-800 text-gray-300 hover:bg-red-900/20 hover:text-red-300 border border-gray-700'
              }`}
            >
              <Heart size={12} className={m.a_like ? 'fill-current' : ''} />
              <span className="font-medium">J'aime{m.nb_likes > 0 ? ` · ${m.nb_likes}` : ''}</span>
            </button>
            <button
              onClick={toggleShow}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-800 text-gray-300 hover:bg-blue-900/20 hover:text-blue-300 border border-gray-700 rounded-full transition-colors"
            >
              <MessageCircle size={12} />
              <span className="font-medium">Commenter</span>
            </button>
            {m.est_de_moi && (
              <button onClick={supprimer} className="ml-auto text-gray-500 hover:text-red-400 inline-flex items-center gap-1 px-1">
                <Trash2 size={11} /> Suppr.
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Section commentaires */}
      {show && (
        <div className="mt-3 pl-12 space-y-2 border-t border-gray-800 pt-3">
          {chargement && (
            <div className="text-center py-2"><Loader2 size={16} className="animate-spin inline text-gray-500" /></div>
          )}
          {!chargement && comms && comms.length === 0 && (
            <div className="text-[11px] text-gray-500 italic">Aucun commentaire — soyez le premier.</div>
          )}
          {comms && comms.map((c) => (
            <Commentaire key={c.id} c={c} onReload={chargerComms} />
          ))}
          {/* Nouveau commentaire */}
          <div className="flex items-center gap-1.5 pt-1 relative">
            <button
              type="button"
              onClick={() => setShowEmoji((v) => !v)}
              className="p-1.5 text-gray-400 hover:text-amber-400"
              title="Insérer un émoji"
            >
              <Smile size={14} />
            </button>
            {showEmoji && (
              <EmojiPicker
                onPick={(emo) => setComNew((v) => v + emo)}
                onClose={() => setShowEmoji(false)}
              />
            )}
            <input
              type="text"
              value={comNew}
              onChange={(e) => setComNew(e.target.value)}
              placeholder="Écrire un commentaire…"
              maxLength={500}
              className="flex-1 px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-full text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
              onKeyDown={(e) => { if (e.key === 'Enter') envoyerComm(); }}
            />
            <button
              onClick={envoyerComm}
              disabled={sending || !comNew.trim()}
              className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-full"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Détail d'un anniversaire ────────────────────────────────────

export function AnniversaireDetail() {
  const navigate = useNavigate();
  const { matricule } = useParams<{ matricule: string }>();
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [contenu, setContenu] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [zoomDest, setZoomDest] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const charger = async () => {
    if (!matricule) return;
    try {
      const r = await portailApi.anniversaireMessages(matricule);
      setData(r);
    } catch (e: any) {
      setErr(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [matricule]);

  const envoyer = async (type: 'message' | 'like') => {
    if (!matricule) return;
    if (type === 'message' && !contenu.trim()) {
      setErr('Écrivez un message');
      return;
    }
    setErr(null);
    setEnvoi(true);
    try {
      await portailApi.anniversaireSouhaiter(matricule, {
        contenu: type === 'like' ? '❤️' : contenu.trim(),
        type_souhait: type,
      });
      setContenu('');
      await charger();
    } catch (e: any) {
      setErr(e?.data?.detail || e?.message || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  if (loading) {
    return (
      <PortailLayout showHeader title="Anniversaire" fullWidth>
        <div className="text-center py-12">
          <Loader2 className="inline-block animate-spin text-pink-400" size={28} />
        </div>
      </PortailLayout>
    );
  }

  if (!data) {
    return (
      <PortailLayout showHeader title="Anniversaire" fullWidth>
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-300">
          {err || 'Anniversaire introuvable'}
        </div>
      </PortailLayout>
    );
  }

  const etu = data.destinataire;
  const estMoi = data.est_moi ?? ((data.destinataire?.matricule || '') === (portailApi.getEtudiant()?.matricule || ''));
  const estAujourdhui = data.est_aujourd_hui !== false;  // défaut true rétrocompat
  // Format de la date d'anniversaire (jour + mois en français)
  const dateAnnivFmt = etu.date_naissance
    ? new Date(etu.date_naissance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
    : '';
  const photoAffichee = etu.photo_save_the_date_url || etu.photo_url;

  return (
    <PortailLayout showHeader title="Anniversaire" fullWidth>
      <div className="space-y-3">
        <button
          onClick={() => navigate('/portail/anniversaires')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        {/* Carte du destinataire */}
        <div className="bg-gradient-to-br from-pink-900/40 to-purple-900/30 border border-pink-700/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            {photoAffichee ? (
              <button type="button" onClick={() => setZoomDest(true)} className="flex-shrink-0">
                <img src={photoAffichee} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-pink-500 cursor-zoom-in" />
              </button>
            ) : (
              <div className="w-16 h-16 rounded-full bg-pink-600 text-white flex items-center justify-center text-xl font-bold">
                {(etu.prenoms?.[0] || '') + (etu.nom?.[0] || '')}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-gray-100">
                <span className="emoji-bounce mr-1">🎂</span>{etu.prenoms} {etu.nom}
              </div>
              <div className="text-xs text-gray-300">
                {etu.classe} {etu.age && <>· {etu.age} ans</>}
              </div>
              {estAujourdhui ? (
                <div className="text-[11px] text-pink-300 mt-1">
                  {data.nb_total} souhait{data.nb_total > 1 ? 's' : ''} reçu{data.nb_total > 1 ? 's' : ''}
                </div>
              ) : (
                <div className="text-[11px] text-pink-300 mt-1">
                  <span className="font-semibold">Save the date · {dateAnnivFmt}</span>
                  {typeof etu.jours_restants === 'number' && etu.jours_restants > 0 && (
                    <span className="ml-1 text-pink-200/70">
                      · dans {etu.jours_restants} jour{etu.jours_restants > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {zoomDest && photoAffichee && (
          <ImageLightbox src={photoAffichee} alt={`${etu.prenoms} ${etu.nom}`} onClose={() => setZoomDest(false)} />
        )}

        {/* Mode « Save the date » (fix 2026-08-31) : c'est pas encore l'anniversaire */}
        {!estAujourdhui && (
          <SaveTheDateBloc
            etu={etu}
            estMoi={estMoi}
            currentPhotoUrl={etu.photo_save_the_date_url || null}
            onUploaded={charger}
          />
        )}
        {/* Contenu classique uniquement le jour J */}
        {estAujourdhui && (
        <>


        {/* Bannière d'invitation à interagir — pour le destinataire */}
        {estMoi && data.messages.length > 0 && (
          <div className="bg-gradient-to-r from-pink-900/30 to-amber-900/20 border border-pink-700/40 rounded-xl p-3 flex items-start gap-3">
            <span className="emoji-float text-2xl flex-shrink-0">🥳</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-pink-100">C'est votre journée !</div>
              <p className="text-[11px] text-pink-200/90 mt-0.5 leading-relaxed">
                Vous pouvez <strong className="text-pink-300">aimer</strong> chaque souhait avec le cœur
                {' '}<Heart size={11} className="inline fill-red-400 text-red-400" />
                {' '}et <strong className="text-pink-300">répondre</strong> via le bouton « Commenter »
                {' '}<MessageCircle size={11} className="inline text-blue-300" />
                {' '}pour remercier vos camarades.
              </p>
            </div>
          </div>
        )}

        {/* Composer (seulement si ce n'est pas moi) */}
        {!estMoi && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 space-y-2 relative">
            <textarea
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              placeholder={`Écrivez un message pour ${etu.prenoms}…`}
              maxLength={500}
              rows={2}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-pink-500"
            />
            {err && <div className="text-xs text-red-400">{err}</div>}
            <div className="flex items-center gap-2 relative">
              <button
                type="button"
                onClick={() => setShowEmoji((v) => !v)}
                className="p-2 text-gray-400 hover:text-amber-400 rounded-lg hover:bg-gray-800"
                title="Insérer un émoji"
              >
                <Smile size={16} />
              </button>
              {showEmoji && (
                <EmojiPicker
                  onPick={(emo) => setContenu((v) => v + emo)}
                  onClose={() => setShowEmoji(false)}
                />
              )}
              <button
                onClick={() => envoyer('message')}
                disabled={envoi || !contenu.trim()}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
              >
                {envoi ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Envoyer
              </button>
              <button
                onClick={() => envoyer('like')}
                disabled={envoi}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-red-900/40 text-red-400 hover:text-red-300 border border-gray-700 hover:border-red-700 text-sm font-medium rounded-lg"
                title="Envoyer juste un coeur"
              >
                <Heart size={14} /> ❤️
              </button>
            </div>
          </div>
        )}

        {/* Liste des messages */}
        {data.messages.length === 0 && (
          <div className="text-center py-8 text-sm text-gray-500">
            Aucun souhait pour l'instant — {estMoi ? 'patience !' : 'soyez le premier'}
          </div>
        )}
        {data.messages.map((m: any) => (
          <MessageBloc key={m.id} m={m} onReload={charger} />
        ))}
        </>
        )}
      </div>
    </PortailLayout>
  );
}


// ─── Bloc « Save the Date » (fix 2026-08-31) ─────────────────────────
// Affiché quand l'anniversaire est à venir (pas aujourd'hui). Pour son
// propre profil : upload d'une photo perso. Pour un camarade : simple
// preview + note « revenez le jour J pour souhaiter ».
const SaveTheDateBloc: React.FC<{
  etu: any;
  estMoi: boolean;
  currentPhotoUrl: string | null;
  onUploaded: () => void | Promise<void>;
}> = ({ etu, estMoi, currentPhotoUrl, onUploaded }) => {
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(null);
    setUploading(true);
    try {
      await portailApi.uploadSaveTheDate(f);
      await onUploaded();
    } catch (e: any) {
      setErr(e?.message || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Retirer votre photo Save the Date ?')) return;
    setUploading(true);
    try {
      await portailApi.deleteSaveTheDate();
      await onUploaded();
    } catch (e: any) {
      setErr(e?.message || 'Erreur');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/60 flex items-center justify-center flex-shrink-0">
          <span className="text-lg">📅</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-300">
            Save the date
          </div>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">
            {estMoi
              ? `Votre anniversaire n'est pas encore aujourd'hui. Vous pouvez déjà mettre en avant une belle photo — elle apparaîtra dans les anniversaires à venir jusqu'au jour J.`
              : `Ce n'est pas encore l'anniversaire de ${etu.prenoms}. Revenez le jour J pour lui souhaiter !`}
          </p>
        </div>
      </div>

      {estMoi && (
        <div className="pt-3 border-t border-gray-800 space-y-2">
          {currentPhotoUrl && (
            <div className="flex items-center gap-3">
              <img src={currentPhotoUrl} alt="Save the date"
                className="w-16 h-16 rounded-lg object-cover border border-gray-700" />
              <div className="flex-1 text-xs text-gray-400">
                Photo actuelle
              </div>
              <button
                onClick={handleDelete}
                disabled={uploading}
                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 px-2 py-1"
              >
                Retirer
              </button>
            </div>
          )}
          <label className="block">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              disabled={uploading}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
            >
              {uploading
                ? <Loader2 size={14} className="animate-spin" />
                : currentPhotoUrl ? '🔄 Changer la photo' : '📸 Ajouter une photo Save the Date'}
            </button>
          </label>
          {err && <div className="text-xs text-red-400">{err}</div>}
          <p className="text-[10px] text-gray-500 leading-relaxed">
            Image jusqu'à 5 Mo. Elle remplacera votre avatar dans le fil des anniversaires à venir.
          </p>
        </div>
      )}
    </div>
  );
};

